import DriveSpaceHelperStrings from '@ms/items-view-utilities/lib/string/DriveSpaceHelper.resx';
import Locale from '../locale/Locale';

export interface IDisplayOptions {
    // Removes trailing zeroes after the decimal points
    trimDecimal?: boolean;

    // Returns empty string if the value is zero
    ignoreZero?: boolean;
}

const oneKiloByte = 1024;
const oneMegaByte = 1048576;
const oneGigaByte = 1073741824;
const oneTeraByte = 1099511627776;

const radix = (1.2).toLocaleString(Locale.language).replace(/\d+/g, '');
// Regex to remove trailing zeroes (and optional '.').
const trimRegex = /\.?0+$/;

namespace DriveSpaceHelper {
    /**
     * Creates a display string for the given number in bytes.
     * This function produces a string value meant to mimic that displyed by file explorer.
     */
    export function getDisplayString(value: number, options: IDisplayOptions = {}): string {
        if (typeof value !== 'number' || isNaN(value) || (options.ignoreZero && value === 0) || value < 0) {
            return '';
        }

        const {
            trimDecimal
        } = options;

        // 1 byte
        if (value === 1) {
            return DriveSpaceHelperStrings.fileSizeBytesSingular;
        }

        // 0 bytes, 2 bytes - 1023 bytes
        if (value < oneKiloByte) {
            return _format(DriveSpaceHelperStrings.fileSizeBytesPlural, value, trimDecimal);
        }

        // 1KB - 999 KB
        if (value < 1000 * oneKiloByte) {
            return _format(DriveSpaceHelperStrings.fileSizeKB, value / oneKiloByte, trimDecimal);
        }

        // 0.97 MB - 999 MB
        if (value < 1000 * oneMegaByte) {
            return _format(DriveSpaceHelperStrings.fileSizeMB, value / oneMegaByte, trimDecimal);
        }

        // 0.97 GB - 999 GB
        if (value < 1000 * oneGigaByte) {
            return _format(DriveSpaceHelperStrings.fileSizeGB, value / oneGigaByte, trimDecimal);
        }

        // 0.97 TB ->
        return _format(DriveSpaceHelperStrings.fileSizeTB, value / oneTeraByte, trimDecimal);
    }

    function _format(template: string, value: number, trimDecimal: boolean): string {
        return template.replace('{0}', _trimNumber(value, trimDecimal));
    }

    function _trimNumber(value: number, trimDecimal: boolean): string {
        // TODO: switch to toLocaleString for entire function when we update phantomJS
        // 12.00 -> 12
        if (value >= 100 || value === Math.floor(value)) {
            return value.toFixed(0);
        }

        // 45.678 -> 45.7
        // 4.5678 -> 4.57
        const str = value.toFixed(value >= 10 ? 1 : 2);
        // Since Number(num.toFixed) can trim decimals even when unwanted,
        // get the radix character and replace it manually
        return (trimDecimal ? str.replace(trimRegex, '') : str).replace('.', radix);
    }
}

export default DriveSpaceHelper;
